import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookMarked, FolderOpen, FolderKanban, Camera, Sparkles, ArrowRight, Target, Brain, CalendarClock, ShieldAlert, Lock } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

function StatCard({ label, value, color, icon: Icon }) {
  return (
    <div className="nb-card p-6" style={{ background: color }}>
      <Icon className="w-7 h-7" strokeWidth={2.5}/>
      <div className="mt-3 text-xs font-bold uppercase tracking-widest text-slate-700">{label}</div>
      <div className="font-display text-4xl font-black mt-1">{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({ total_words: 0, total_categories: 0, total_materials: 0 });
  const [quota, setQuota] = useState(null);
  const [recent, setRecent] = useState([]);
  const [weakSpots, setWeakSpots] = useState([]);
  const [upcoming, setUpcoming] = useState([]);

  useEffect(() => {
    (async () => {
      const [s, w, q, ws, ts] = await Promise.all([
        api.get("/stats"),
        api.get("/words"),
        api.get("/storage/quota"),
        api.get("/quiz/weak-spots", { params: { limit: 5 } }),
        api.get("/tests", { params: { upcoming_only: true } }),
      ]);
      setStats(s.data);
      setRecent(w.data.slice(0, 5));
      setQuota(q.data);
      setWeakSpots(ws.data);
      setUpcoming(ts.data.slice(0, 4));
    })();
  }, []);

  const usedMB = quota ? (quota.used_bytes / (1024 * 1024)).toFixed(1) : 0;
  const limitMB = quota ? (quota.limit_bytes / (1024 * 1024)).toFixed(0) : 100;
  const usedPct = quota ? Math.min(100, (quota.used_bytes / quota.limit_bytes) * 100) : 0;

  // Plan expiry banner logic
  const proUntilDate = user?.pro_until ? new Date(user.pro_until) : null;
  const daysLeft = proUntilDate ? Math.ceil((proUntilDate - new Date()) / 86400000) : null;
  const showExpirySoon = user?.is_cancelling && daysLeft !== null && daysLeft <= 7 && daysLeft >= 0;
  const overFreeBytes = quota ? Math.max(0, (quota.used_bytes || 0) - 100 * 1024 * 1024) : 0;
  const showJustDowngraded = quota?.plan === "free" && overFreeBytes > 0;
  const overFreeHuman = overFreeBytes >= 1024 ** 3
    ? `${(overFreeBytes / 1024 ** 3).toFixed(1)} GB`
    : `${(overFreeBytes / 1024 ** 2).toFixed(0)} MB`;

  return (
    <div className="p-6 sm:p-8 lg:p-12">
      <header className="mb-8">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Welcome back</p>
        <h1 className="font-display text-4xl sm:text-5xl font-black mt-1">Hey {user?.name?.split(" ")[0]}.</h1>
        <p className="text-base font-medium text-slate-600 mt-2">Here's what's in your study brain today.</p>
      </header>

      {showExpirySoon && (
        <div data-testid="dashboard-expiry-banner" className="mb-6 nb-card p-4 bg-amber-100 flex items-start gap-3">
          <ShieldAlert className="w-5 h-5 mt-0.5 shrink-0" strokeWidth={2.5}/>
          <div className="flex-1 text-sm">
            <p className="font-bold">Your {user?.plan === "pro_plus" ? "Pro Plus" : "Pro"} plan ends in {daysLeft} day{daysLeft !== 1 ? "s" : ""}.</p>
            <p className="mt-1 text-slate-700">After {proUntilDate.toLocaleDateString()}, files over 100 MB become read-only (never deleted). Reactivate anytime.</p>
          </div>
          <a href="/settings" className="nb-btn text-xs whitespace-nowrap" data-testid="dashboard-expiry-cta">Manage</a>
        </div>
      )}

      {showJustDowngraded && (
        <div data-testid="dashboard-locked-banner" className="mb-6 nb-card p-4 bg-rose-100 flex items-start gap-3">
          <Lock className="w-5 h-5 mt-0.5 shrink-0" strokeWidth={2.5}/>
          <div className="flex-1 text-sm">
            <p className="font-bold">{overFreeHuman} of your files are locked.</p>
            <p className="mt-1 text-slate-700">They're safe — never deleted. Resume Pro to unlock instantly.</p>
          </div>
          <a href="/pricing" className="nb-btn text-xs whitespace-nowrap" data-testid="dashboard-locked-cta">Resume Pro</a>
        </div>
      )}

      <div className="grid sm:grid-cols-3 gap-6 mb-8">
        <StatCard label="Words" value={stats.total_words} color="#FDE047" icon={BookMarked}/>
        <StatCard label="Categories" value={stats.total_categories} color="#86EFAC" icon={FolderKanban}/>
        <StatCard label="Materials" value={stats.total_materials} color="#FDBA74" icon={FolderOpen}/>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="nb-card p-6 lg:col-span-2">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-2xl font-bold">Recent words</h2>
            <Link to="/glossary" className="text-sm font-bold underline">View all</Link>
          </div>
          {recent.length === 0 ? (
            <div className="mt-6 text-center py-12 border-2 border-dashed border-slate-900 rounded-md bg-yellow-50">
              <p className="font-bold">No words yet.</p>
              <p className="text-sm text-slate-600 mt-1">Add one or scan a page.</p>
              <div className="flex gap-3 justify-center mt-4">
                <Link to="/glossary" className="nb-btn-primary inline-flex items-center gap-2">Add a word</Link>
                <Link to="/scan" className="nb-btn-secondary inline-flex items-center gap-2"><Camera className="w-4 h-4" strokeWidth={2.5}/> Scan</Link>
              </div>
            </div>
          ) : (
            <ul className="mt-4 divide-y-2 divide-slate-200">
              {recent.map((w) => (
                <li key={w.id} className="py-3">
                  <div className="font-display font-bold text-lg">{w.term}</div>
                  <div className="text-sm text-slate-700 line-clamp-2">{w.definition}</div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex flex-col gap-6">
          <div className="nb-card p-6" style={{ background: "#D8B4FE" }}>
            <p className="text-xs font-bold uppercase tracking-widest">Plan</p>
            <div className="font-display text-2xl font-black mt-1">{quota?.plan_label || "Free"}</div>
            <div className="mt-4">
              <div className="flex justify-between text-xs font-bold">
                <span>{usedMB} MB used</span>
                <span>of {limitMB >= 1024 ? `${(limitMB/1024).toFixed(0)} GB` : `${limitMB} MB`}</span>
              </div>
              <div className="h-3 bg-white border-2 border-slate-900 rounded-full mt-2 overflow-hidden">
                <div className="h-full bg-slate-900" style={{ width: `${usedPct}%` }}/>
              </div>
            </div>
            {quota?.plan === "free" && (
              <Link to="/pricing" data-testid="dashboard-upgrade-link" className="nb-btn-primary mt-5 inline-flex items-center gap-2 w-full justify-center">
                <Sparkles className="w-4 h-4" strokeWidth={2.5}/> Upgrade to Pro
              </Link>
            )}
          </div>

          <Link to="/scan" className="nb-card p-6 block hover:bg-yellow-50 transition-colors" data-testid="dashboard-scan-cta">
            <Camera className="w-7 h-7" strokeWidth={2.5}/>
            <h3 className="font-display text-xl font-bold mt-2">Scan a textbook page</h3>
            <p className="text-sm text-slate-600 mt-1">AI extracts vocabulary in seconds.</p>
            <span className="inline-flex items-center gap-1 text-sm font-bold mt-3">Try it now <ArrowRight className="w-4 h-4" strokeWidth={2.5}/></span>
          </Link>
        </div>
      </div>

      <section className="mt-8">
        <div className="nb-card p-6" style={{ background: "#7DD3FC33" }}>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <CalendarClock className="w-6 h-6" strokeWidth={2.5}/>
              <div>
                <h2 className="font-display text-2xl font-bold">Upcoming tests</h2>
                <p className="text-sm font-medium text-slate-700">Your next exams and the reminders you've set.</p>
              </div>
            </div>
            <Link to="/tests" data-testid="dashboard-tests-cta" className="nb-btn-primary inline-flex items-center gap-2">
              <CalendarClock className="w-4 h-4" strokeWidth={2.5}/> Manage
            </Link>
          </div>
          {upcoming.length === 0 ? (
            <p className="text-sm text-slate-600 mt-4">No tests scheduled yet. Add one to get reminders.</p>
          ) : (
            <ul className="mt-5 grid sm:grid-cols-2 gap-3" data-testid="upcoming-tests-list">
              {upcoming.map((t) => {
                const ms = new Date(t.test_date) - new Date();
                const d = ms < 0 ? 0 : Math.ceil(ms / (24 * 3600 * 1000));
                const urgent = d <= 3;
                return (
                  <li key={t.id} className="nb-card-sm p-3 bg-white flex items-center justify-between gap-3" data-testid={`upcoming-${t.title}`}>
                    <div className="min-w-0">
                      <div className="font-display font-bold truncate">{t.title}</div>
                      <div className="text-xs text-slate-500">{new Date(t.test_date).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</div>
                    </div>
                    <span className="nb-tag whitespace-nowrap" style={{ background: urgent ? "#F9A8D4" : "#FDE047" }}>
                      {d === 0 ? "Today" : d === 1 ? "1 day" : `${d} days`}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </section>

      <section className="mt-8">
        <div className="nb-card p-6" style={{ background: "#F9A8D433" }}>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <Target className="w-6 h-6" strokeWidth={2.5}/>
              <div>
                <h2 className="font-display text-2xl font-bold">Weak spots</h2>
                <p className="text-sm font-medium text-slate-700">Words you keep getting wrong on quizzes — review these first.</p>
              </div>
            </div>
            <Link to="/quiz" data-testid="dashboard-quiz-cta" className="nb-btn-primary inline-flex items-center gap-2">
              <Brain className="w-4 h-4" strokeWidth={2.5}/> Start a quiz
            </Link>
          </div>

          {weakSpots.length === 0 ? (
            <p className="text-sm text-slate-600 mt-4">No data yet. Take a quiz to see which words trip you up.</p>
          ) : (
            <ul className="mt-5 grid sm:grid-cols-2 gap-3" data-testid="weak-spots-list">
              {weakSpots.map((w) => {
                const pct = Math.round((w.accuracy || 0) * 100);
                return (
                  <li key={w.term} className="nb-card-sm p-3 bg-white flex items-center justify-between gap-3" data-testid={`weak-spot-${w.term}`}>
                    <div className="min-w-0">
                      <div className="font-display font-bold truncate">{w.term}</div>
                      <div className="text-xs text-slate-500">{w.correct}/{w.total} correct · {pct}%</div>
                    </div>
                    <div className="w-24 h-2 bg-slate-100 border-2 border-slate-900 rounded-full overflow-hidden shrink-0">
                      <div className="h-full bg-pink-400" style={{ width: `${100 - pct}%` }}/>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
}
